// @flow

import React, { useState, useCallback, useRef, useLayoutEffect } from 'react';
import { pdfjs, Document, Page } from 'react-pdf';
// import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import './AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import Tooltip from '@mui/material/Tooltip';
import useResizeObserver from '@react-hook/resize-observer';
import CircularProgress from '@mui/material/CircularProgress';
import { IconButton, Grid, Typography } from '@mui/material';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import {
  getTextToHighlight,
  highlightText
  // @ts-ignore
} from '@packages/mdp-cda-viewer/container/PDFViewer/highlighter';
import {
  getTextToHighlight as getTextToHighlightFuzzy,
  highlightText as highlightTextFuzzy
  // @ts-ignore
} from '@packages/mdp-cda-viewer/container/PDFViewer/fuzzy-highlighter';
import { useDispatch } from 'react-redux';
import { showToastMsg } from '../../features/toast-message-slice';

pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

const HIGHLIGHT_TEXT_SELECTOR =
  '.react-pdf__Page__textContent [role="presentation"]';
const TEXT_LAYER_SELECTOR = '.react-pdf__Page';
const CANVAS_SELECTOR = '.react-pdf__Page__canvas';
const CUSTOM_SQUARE_SELECTOR = '.custom-square';
const CUSTOM_SQUARE_CLASS_NAME = 'custom-square';
const PDF_DOCUMENT_SELECTOR = '.react-pdf__Document';

const autoScrollToFirstMark = () => {
  const firstMark = document.querySelector('span mark');

  if (firstMark) {
    firstMark.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
};

const useWidth = (target: any) => {
  const [width, setWidth] = useState<number | null>(null);

  useLayoutEffect(() => {
    if (target) setWidth(target.current.getBoundingClientRect().width);
  }, [target]);

  useResizeObserver(target, (entry) => setWidth(entry.contentRect.width));

  return width;
};

interface Props {
  url: string;
  previewPage: number | null;
  boundingBox?: {
    x1: number;
    y1: number;
    x2: number;
    y2: number;
  } | null;
  imageDimension?: {
    width: number;
    height: number;
  } | null;
  ocr: boolean;
  searchText?: string | null;
  pdfFullHeight: boolean;
  setPdfFullHeight: (pdfFullHeight: boolean) => void;
}

const PdfFileRenderer = (props: Props): JSX.Element => {
  const {
    url,
    previewPage,
    boundingBox,
    imageDimension,
    ocr,
    searchText,
    pdfFullHeight,
    setPdfFullHeight
  } = props;
  const [scale, setScale] = useState(1);
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState<number | null>(null);
  const [scanningAllPages, setScanningAllPages] = useState(false);
  const pdfWrapper = useRef<HTMLDivElement>(null);
  const width = useWidth(pdfWrapper);
  const dispatch = useDispatch();

  const onDocumentLoadSuccess = useCallback(
    async ({ numPages }: { numPages: number }) => {
      if (!previewPage && searchText) {
        setScanningAllPages(true);
        dispatch(
          showToastMsg({
            open: true,
            message: 'Searching for excerpt...',
            level: 'info',
            duration: 5000
          })
        );
      }

      setNumPages(numPages);
      setPageNumber(previewPage || 1);
    },
    []
  );

  const changePage = useCallback((offset: number) => {
    setPageNumber((prevPageNumber) => {
      if (prevPageNumber === null) return offset;
      return prevPageNumber + offset;
    });
  }, []);

  const previousPage = useCallback(() => {
    changePage(-1);
  }, [changePage]);

  const nextPage = useCallback(() => {
    changePage(1);
  }, [changePage]);

  const changeScale = useCallback((offset: number) => {
    setScale((prevScale) => prevScale + offset);
  }, []);

  const decreaseScale = useCallback(() => {
    changeScale(-0.1);
  }, [changeScale]);

  const increaseScale = useCallback(() => {
    changeScale(0.1);
  }, [changeScale]);

  const autoHighlight = () => {
    const spanElements = document.querySelectorAll(HIGHLIGHT_TEXT_SELECTOR);
    const textToHighlight = getTextToHighlight(spanElements, searchText);
    const excerptFound = Boolean(Object.keys(textToHighlight)?.length);

    if (excerptFound) {
      highlightText(spanElements, textToHighlight);
      autoScrollToFirstMark();

      return true;
    }

    if (previewPage === pageNumber) {
      console.log('Trying fuzzy highlighter');
      const textToHighlightFuzzy = getTextToHighlightFuzzy(
        spanElements,
        searchText
      );

      if (textToHighlightFuzzy.length) {
        console.log('Fuzzy highlighter found an excerpt, highlighting now');
        highlightTextFuzzy(textToHighlightFuzzy);
        autoScrollToFirstMark();

        return true;
      }
    }

    return false;
  };

  const removeCustomSqure = useCallback(() => {
    const pdfDocument = document.querySelector(PDF_DOCUMENT_SELECTOR);

    if (pdfDocument) {
      const oldSquare = pdfDocument.querySelector(CUSTOM_SQUARE_SELECTOR);
      if (oldSquare) oldSquare.remove();
    }
  }, []);

  const drawBoundingBox = useCallback(() => {
    if (!boundingBox || !imageDimension) return false;
    const textLayer = document.querySelector(TEXT_LAYER_SELECTOR);
    const canvas = document.querySelector(CANVAS_SELECTOR);
    if (!textLayer || !canvas) return false;

    if (
      typeof boundingBox.x1 !== 'number' ||
      typeof boundingBox.y1 !== 'number' ||
      typeof boundingBox.x2 !== 'number' ||
      typeof boundingBox.y2 !== 'number' ||
      typeof imageDimension.width !== 'number' ||
      typeof imageDimension.height !== 'number'
    ) {
      return false;
    }

    const textLayerBoundingClientRect = textLayer.getBoundingClientRect();
    const textLayerWidth = textLayerBoundingClientRect.width;
    const textLayerHeight = textLayerBoundingClientRect.height;
    const canvasBoundingClientRect = canvas.getBoundingClientRect();
    const canvasWidth = canvasBoundingClientRect.width;
    const canvasHeight = canvasBoundingClientRect.height;
    const widthRatio = canvasWidth / textLayerWidth;
    const heightRatio = canvasHeight / textLayerHeight;
    const top = (boundingBox.y1 / imageDimension.height) * 100 * heightRatio;
    const left = (boundingBox.x1 / imageDimension.width) * 100 * widthRatio;
    const width =
      ((boundingBox.x2 - boundingBox.x1) / imageDimension.width) *
      100 *
      widthRatio;
    const height =
      ((boundingBox.y2 - boundingBox.y1) / imageDimension.height) *
      100 *
      heightRatio;
    const square = document.createElement('div');
    square.className = CUSTOM_SQUARE_CLASS_NAME;
    square.style.position = 'absolute';
    square.style.top = `${top}%`;
    square.style.left = `${left}%`;
    square.style.width = `${width}%`;
    square.style.height = `${height}%`;
    square.style.border = '1px solid red';
    square.style.background = 'rgba(255,44,33,0.1)';
    square.style.pointerEvents = 'none';
    square.style.zIndex = '10';
    textLayer.appendChild(square);
    square.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    });

    return true;
  }, [boundingBox, imageDimension]);

  const onRenderTextLayerSuccessHandler = useCallback(() => {
    if (!searchText || !pageNumber) return;
    removeCustomSqure();
    const isPreviewPage = previewPage === pageNumber;
    let excerptFound = false;

    if (ocr) {
      if (isPreviewPage && drawBoundingBox()) return;
      excerptFound = autoHighlight();
    } else {
      excerptFound = autoHighlight();
      if (!excerptFound && isPreviewPage && drawBoundingBox()) return;
    }

    if (previewPage !== null || !scanningAllPages) return;

    if (excerptFound) {
      dispatch(
        showToastMsg({
          open: true,
          message: 'Excerpt found',
          level: 'success',
          duration: 5000
        })
      );
      setScanningAllPages(false);
    } else {
      if (numPages && pageNumber < numPages) {
        setPageNumber((prevPageNumber) => {
          if (prevPageNumber === null) return 1;
          return prevPageNumber + 1;
        });
      } else {
        if (!excerptFound) {
          dispatch(
            showToastMsg({
              open: true,
              message: 'Excerpt not found',
              level: 'error',
              duration: 5000
            })
          );
          setPageNumber(1);
        }

        setScanningAllPages(false);
      }
    }
  }, [pageNumber]);

  return (
    <>
      <Grid item style={{ margin: 'auto' }}>
        <Typography variant="body2">
          <Tooltip title="Previous Page">
            <span>
              <IconButton
                aria-label="previous page"
                disabled={Boolean(pageNumber && pageNumber <= 1)}
                onClick={previousPage}
              >
                <ArrowBackIosIcon />
              </IconButton>
            </span>
          </Tooltip>
          {pageNumber || (numPages ? 1 : '--')} / {numPages || '--'}
          <Tooltip title="Next Page">
            <span>
              <IconButton
                aria-label="next page"
                disabled={Boolean(
                  !numPages || (pageNumber && pageNumber >= numPages)
                )}
                onClick={nextPage}
              >
                <ArrowForwardIosIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Zoom Out">
            <span>
              <IconButton
                aria-label="zoom out"
                disabled={scale <= 0.5}
                onClick={decreaseScale}
              >
                <ZoomOutIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Zoom In">
            <span>
              <IconButton
                aria-label="zoom in"
                disabled={scale >= 2}
                onClick={increaseScale}
              >
                <ZoomInIcon />
              </IconButton>
            </span>
          </Tooltip>
          {pdfFullHeight ? (
            <Tooltip title="Exit Full Screen">
              <IconButton
                aria-label="exit full screen"
                onClick={() => setPdfFullHeight(false)}
              >
                <CloseFullscreenIcon />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title="Switch to Full Screen">
              <IconButton
                aria-label="switch to full screen"
                onClick={() => setPdfFullHeight(true)}
              >
                <OpenInFullIcon />
              </IconButton>
            </Tooltip>
          )}
        </Typography>
      </Grid>
      <Grid
        item
        style={{
          width: '100%',
          height: '100%',
          overflow: 'auto',
          border: '1px solid black',
          flexGrow: '1'
        }}
        ref={pdfWrapper}
      >
        <Document
          onLoadError={console.error}
          file={url}
          onLoadSuccess={onDocumentLoadSuccess}
          loading={<CircularProgress />}
        >
          {pageNumber && (
            <Page
              scale={scale}
              pageNumber={pageNumber}
              width={width ? width : 0}
              onRenderTextLayerSuccess={onRenderTextLayerSuccessHandler}
            />
          )}
        </Document>
      </Grid>
    </>
  );
};

export default PdfFileRenderer;
